import React, { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import "../../styles/projectStyles/ProjectDates.css";

const ProjectDates = () => {
  const [selectedTasks, setSelectedTasks] = useState(() => {
    const storedTasks = JSON.parse(localStorage.getItem("selectedTasks")) || [];
    return storedTasks;
  });

  const [dateRanges, setDateRanges] = useState(() => {
    const storedDateRanges =
      JSON.parse(localStorage.getItem("dateRanges")) || {};
    const defaultDateRanges = {};

    // Ensure every task and team member has at least one default date row
    selectedTasks.forEach((task) => {
      defaultDateRanges[task.taskid] = {};
      task.employeelist.forEach((_, teamIndex) => {
        defaultDateRanges[task.taskid][teamIndex] = storedDateRanges[
          task.taskid
        ]?.[teamIndex] || [
          {
            startDate: null,
            endDate: null,
            startTime: DateTime.fromFormat("09:00 AM", "h:mm a", {
              zone: "UTC",
            }).toFormat("h:mm a"),
          },
        ];
      });
    });

    return defaultDateRanges;
  });

  const [validationError, setValidationError] = useState("");

  // Validate the date ranges and update localStorage with "projectDatesErrors"
  const validateDateRanges = () => {
    let isValid = true;

    // Check all tasks, team members, and their date ranges
    selectedTasks.forEach((task) => {
      const teamRanges = dateRanges[task.taskid] || {};
      Object.values(teamRanges).forEach((teamDateRanges) => {
        teamDateRanges.forEach(({ startDate, endDate, startTime }) => {
          if (!startDate || !endDate || !startTime) {
            isValid = false;
          }
        });
      });
    });

    setValidationError(
      isValid
        ? ""
        : "All employee must assigned to valid start dates, end dates, and start times."
    );

    // Update localStorage with validation result
    localStorage.setItem("projectDatesErrors", JSON.stringify({ isValid }));
    return isValid;
  };

  useEffect(() => {
    localStorage.setItem("dateRanges", JSON.stringify(dateRanges));
    validateDateRanges(); // Validate whenever dateRanges changes
  }, [dateRanges]);

  const handleDateChange = (value, taskId, teamIndex, rowIndex, field) => {
    setDateRanges((prevDateRanges) => {
      const taskDateRanges = prevDateRanges[taskId] || {};
      const teamDateRanges = taskDateRanges[teamIndex] || [];

      let formattedValue;

      if (field === "startTime") {
        formattedValue =
          value && typeof value === "string" && value.trim() !== ""
            ? DateTime.fromFormat(value, "h:mm a", { zone: "UTC" }).toFormat(
                "h:mm a"
              )
            : teamDateRanges[rowIndex]?.startTime ||
              DateTime.fromFormat("09:00 AM", "h:mm a", {
                zone: "UTC",
              }).toFormat("h:mm a");
      } else {
        formattedValue = value && value.isValid ? value.toISO() : null;
      }

      const updatedRow = {
        ...teamDateRanges[rowIndex],
        [field]: formattedValue,
      };

      const updatedTeamDateRanges = [...teamDateRanges];
      updatedTeamDateRanges[rowIndex] = updatedRow;

      const updatedTaskDateRanges = {
        ...taskDateRanges,
        [teamIndex]: updatedTeamDateRanges,
      };

      const updatedDateRanges = {
        ...prevDateRanges,
        [taskId]: updatedTaskDateRanges,
      };

      localStorage.setItem("dateRanges", JSON.stringify(updatedDateRanges));
      return updatedDateRanges;
    });
  };

  const addDateRow = (taskId, teamIndex) => {
    setDateRanges((prevDateRanges) => {
      const taskDateRanges = prevDateRanges[taskId] || {};
      const teamDateRanges = taskDateRanges[teamIndex] || [];

      const newRow = {
        startDate: null,
        endDate: null,
        startTime: DateTime.fromFormat("09:00 AM", "h:mm a", {
          zone: "UTC",
        }).toFormat("h:mm a"),
      };

      const updatedTeamDateRanges = [...teamDateRanges, newRow];
      const updatedTaskDateRanges = {
        ...taskDateRanges,
        [teamIndex]: updatedTeamDateRanges,
      };

      const updatedDateRanges = {
        ...prevDateRanges,
        [taskId]: updatedTaskDateRanges,
      };

      localStorage.setItem("dateRanges", JSON.stringify(updatedDateRanges));
      return updatedDateRanges;
    });
  };

  const deleteDateRow = (taskId, teamIndex, rowIndex) => {
    setDateRanges((prevDateRanges) => {
      const taskDateRanges = prevDateRanges[taskId] || {};
      const teamDateRanges = taskDateRanges[teamIndex] || [];

      // Prevent deletion if it's the last row
      if (teamDateRanges.length <= 1) {
        return prevDateRanges; // Return without making any changes
      }

      const updatedTeamDateRanges = teamDateRanges.filter(
        (_, index) => index !== rowIndex
      );

      const updatedTaskDateRanges = {
        ...taskDateRanges,
        [teamIndex]: updatedTeamDateRanges,
      };

      const updatedDateRanges = {
        ...prevDateRanges,
        [taskId]: updatedTaskDateRanges,
      };

      localStorage.setItem("dateRanges", JSON.stringify(updatedDateRanges));
      return updatedDateRanges;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <div className="mainCointainer">
        <div className="project-dates-container">
          <h3 className="proj-title">Project Dates</h3>
          <table className="proj-table">
            <thead>
              <tr>
              <th className="proj-item">
              <img src="/images/worker.png" alt="Icon" />
              Tasks
            </th>
            <th className="proj-item">
              <img src="/images/team.svg" alt="Icon" />
              Team
            </th>
            <th className="proj-item">
              <img src="/images/dates.svg" alt="Icon"  />
              Dates
            </th>
            <th className="proj-item">
              <img src="/images/time.png" alt="Icon" />
              Start Time
            </th>
              </tr>
            </thead>
          </table>

          {selectedTasks.length === 0 ? (
            <div className="no-tasks-message">No tasks or teams assigned.</div>
          ) : (
            <div className="project-form-container">
              {selectedTasks.map((task) => (
                <div key={task.taskid}>
                  <table className="project-table">
                    <tbody>
                      <tr className="task-category">
                        <td colSpan="4" className="tasks">
                          {task.taskname}
                          {task.employeelist.map((teamMember, teamIndex) => (
                            <tr key={teamIndex}>
                              <td className="task-list">
                                {teamIndex === 0 &&
                                  task.subtasklist.map((subTask) => (
                                    <p key={subTask.value}>{subTask.label}</p>
                                  ))}
                              </td>
                            </tr>
                          ))}
                        </td>
                        <div className="employee-row">
                          {task.employeelist.map((teamMember, teamIndex) => (
                            <tr key={teamIndex}>
                              <td className="team-info">{teamMember.label}</td>
                              <td className="employee-date-time">
                                <div className="date-main-row">
                                  <div>
                                    {console.log("DR. ", dateRanges)}
                                    {dateRanges[task.taskid]?.[teamIndex]?.map(
                                      (dateRow, rowIndex) => (
                                        <div
                                          key={rowIndex}
                                          className="date-row"
                                        >
                                          <DatePicker
                                          className="custom-datepicker"
                                            label="Start Date"
                                            value={
                                              dateRow.startDate
                                                ? DateTime.fromISO(
                                                    dateRow.startDate
                                                  )
                                                : null
                                            }
                                            onChange={(value) =>
                                              handleDateChange(
                                                value,
                                                task.taskid,
                                                teamIndex,
                                                rowIndex,
                                                "startDate"
                                              )
                                            }
                                          />
                                          <span className="sepr">to</span>
                                          <DatePicker
                                          className="custom-datepicker"
                                            label="End Date"
                                            value={
                                              dateRow.endDate
                                                ? DateTime.fromISO(
                                                    dateRow.endDate
                                                  )
                                                : null
                                            }
                                            onChange={(value) =>
                                              handleDateChange(
                                                value,
                                                task.taskid,
                                                teamIndex,
                                                rowIndex,
                                                "endDate"
                                              )
                                            }
                                            minDate={
                                              dateRow.startDate
                                                ? DateTime.fromISO(
                                                    dateRow.startDate
                                                  )
                                                : null
                                            }
                                          />
                                          <TimePicker
                                          className="custom-datepicker"
                                            label="Start Time"
                                            value={
                                              dateRow.startTime
                                                ? DateTime.fromFormat(
                                                    dateRow.startTime,
                                                    "h:mm a",
                                                    { zone: "UTC" }
                                                  )
                                                : null
                                            }
                                            onChange={(value) =>
                                              handleDateChange(
                                                value
                                                  ? value.toFormat("h:mm a")
                                                  : null,
                                                task.taskid,
                                                teamIndex,
                                                rowIndex,
                                                "startTime"
                                              )
                                            }
                                            ampm
                                          />
                                  <div>
                                    <img
                                      src="/images/adddate.svg"
                                      alt="Add Row"
                                      className="action-icon"
                                      onClick={() =>
                                        addDateRow(task.taskid, teamIndex)
                                      }
                                    />
                                  </div>
                                          <img
                                            src="/images/delete.svg"
                                            alt="Delete"
                                            className="action-icon"
                                            onClick={() =>
                                              deleteDateRow(
                                                task.taskid,
                                                teamIndex,
                                                rowIndex
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    )}
                                  </div>
                                  
                                </div>
                              </td>
                            </tr>
                          ))}
                        </div>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
        {validationError && (
          <div className="validation-error">
            <span className="error-text">{validationError}</span>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default ProjectDates;
